/**
 * Envelope ของคำสั่งออฟไลน์ฝั่งอุปกรณ์ (M11)
 *
 * นี่คือ "ครึ่งที่อุปกรณ์สร้างเอง" ของตาราง `offline_sync_command` ใน
 * `docs/ERD.md` และรายการ field ใน `references/business-process-flows.md` ข้อ 9
 * — ห้ามคิด field ใหม่ที่ server contract ไม่มี และห้ามตัด `client_event_id`
 * ทิ้ง เพราะเป็น idempotency key ที่ server ใช้ dedupe
 *
 * field ที่เป็นของ server ล้วน ๆ (`received_at`) ไม่อยู่ที่นี่ ส่วน `sync_status`
 * เก็บไว้เพื่อบอกว่า "คำสั่งใบนี้ไปถึงไหนแล้ว" ตาม lifecycle ของ D-078 ไม่ใช่
 * สำเนาของสถานะฝั่ง server
 *
 * ขอบเขตที่ห้ามละเมิด: นี่คือ **command queue ไม่ใช่ ledger คู่ขนาน**
 * (`docs/architecture.md` Dependency Rule 8) — เก็บเฉพาะ "สิ่งที่ผู้ใช้สั่ง"
 * ห้ามเก็บหรือคำนวณยอดคงเหลือ สถานะ lot หรือผลการ post ใดๆ ในเครื่อง
 */

/**
 * `device_sequence_no` ของคำสั่งแรกในเครื่อง
 *
 * เริ่มที่ 1 และเพิ่มทีละ 1 เสมอ ค่า 0 สงวนไว้เป็น "ยังไม่เคยจ่ายเลขให้ใคร"
 * ของ counter ที่เก็บใน IndexedDB
 */
export const FIRST_DEVICE_SEQUENCE_NO = 1;

/** ค่าเริ่มต้นของ counter — เลขล่าสุดที่ "จ่ายไปแล้ว" ตอนยังไม่มีคำสั่งเลย */
export const UNALLOCATED_DEVICE_SEQUENCE_NO = FIRST_DEVICE_SEQUENCE_NO - 1;

/**
 * สถานะของคำสั่งในเครื่อง — lifecycle เต็มตาม D-078
 *
 * `references/business-process-flows.md` ข้อ 9 นิยามไว้ว่า
 * `PENDING_LOCAL → SYNCING → ACCEPTED ↘ REJECTED/CONFLICT` และตอนนี้ sync engine
 * (`syncEngine.ts`) มีอยู่จริงแล้ว สถานะที่เหลือจึงเกิดขึ้นได้ครบวง
 *
 * เส้นแบ่งที่ยังห้ามข้าม: **เครื่องเป็นผู้เขียนได้แค่สองสถานะแรก** — `PENDING_LOCAL`
 * ตอนเข้าคิว และ `SYNCING` ตอนหยิบไปส่ง ส่วนอีกสามสถานะเป็นคำตอบของ server ที่
 * engine คัดลอกลงแถวตาม `client_event_id` เท่านั้น ห้ามหน้าจอหรือคิวอนุมานเอง
 * (`docs/ERD.md`, `docs/SECURITY.md` Offline/Online Policy: local success ไม่ใช่
 * server commit)
 */
export const PENDING_LOCAL = 'PENDING_LOCAL';
/** หยิบไปอยู่ใน batch ที่กำลังส่งแล้ว — ยังไม่ใช่คำตอบของ server */
export const SYNCING = 'SYNCING';
/** server apply แล้ว (`ACCEPTED` ใน response ของ `POST /v1/offline-sync/commands`) */
export const ACCEPTED = 'ACCEPTED';
/** server ปฏิเสธถาวร — ส่งซ้ำก็ไม่ช่วย (payload พัง/command ไม่รู้จัก/อุปกรณ์ไม่ ACTIVE) */
export const REJECTED = 'REJECTED';
/** ชนกฎธุรกิจ — ต้องให้คนตัดสินผ่านหน้าจอ conflict (WO แยก) ห้าม engine แก้เอง */
export const CONFLICT = 'CONFLICT';

export type LocalSyncStatus =
  | typeof PENDING_LOCAL
  | typeof SYNCING
  | typeof ACCEPTED
  | typeof REJECTED
  | typeof CONFLICT;

/**
 * สถานะที่ **server เท่านั้น** เป็นผู้ตัดสิน
 *
 * แยกชนิดออกมาเพื่อให้ TypeScript ปฏิเสธโค้ดที่เขียนสถานะกลุ่มนี้ลงแถวโดยไม่ได้
 * มาจาก response — จุดเดียวที่สร้างค่านี้ได้คือ `parseServerSyncStatus()`
 */
export type ServerSyncStatus = typeof ACCEPTED | typeof REJECTED | typeof CONFLICT;

/**
 * สถานะที่ยังต้องส่งขึ้น server
 *
 * `SYNCING` อยู่ในชุดนี้ด้วยโดยเจตนา: คำสั่งที่ค้างสถานะนี้คือ drain รอบก่อนที่
 * ขาดตอน (แอปถูกปิด/เครื่องดับกลางคัน) การส่งซ้ำปลอดภัยเพราะ server dedupe ด้วย
 * `client_event_id` — ถ้าไม่นับรวม คำสั่งของผู้ปฏิบัติงานจะค้างอยู่ในเครื่องตลอดไป
 */
export const DRAINABLE_SYNC_STATUSES: readonly LocalSyncStatus[] = [PENDING_LOCAL, SYNCING];

/** คำสั่งใบนี้ยังต้องถูกส่งอยู่หรือไม่ */
export function isDrainableSyncStatus(status: unknown): status is typeof PENDING_LOCAL | typeof SYNCING {
  return status === PENDING_LOCAL || status === SYNCING;
}

/** จบเส้นทางแล้ว — engine ไม่แตะอีก (`CONFLICT`/`REJECTED` รอคนมาจัดการ) */
export function isTerminalSyncStatus(status: unknown): status is ServerSyncStatus {
  return status === ACCEPTED || status === REJECTED || status === CONFLICT;
}

/** ต้องมีคนมาดู — conflict ที่รอการตัดสิน และคำสั่งที่ถูกปฏิเสธถาวร */
export function needsHumanAttention(status: unknown): boolean {
  return status === CONFLICT || status === REJECTED;
}

/**
 * แปลงค่า `sync_status` ที่ได้จาก response ให้เป็นสถานะที่เก็บลงเครื่องได้
 *
 * คืน `null` เมื่อเป็นค่าที่ contract ไม่ได้นิยาม (`docs/API_CONTRACT.md` ระบุสาม
 * ค่าเท่านั้น) — ค่าที่อ่านไม่ออกต้องไม่ถูกเดาเป็น "สำเร็จ" และต้องไม่ถูกเขียนลง
 * แถว เพราะสถานะปลายทางที่ผิดคือคำสั่งที่ไม่มีใครตามต่อ
 */
export function parseServerSyncStatus(value: unknown): ServerSyncStatus | null {
  return value === ACCEPTED || value === REJECTED || value === CONFLICT ? value : null;
}

/** สิ่งที่ผู้เรียกต้องส่งมา — ส่วนที่คิวสร้างเองไม่รับจากข้างนอก */
export interface OfflineCommandInput {
  readonly device_id: string;
  readonly employee_id: string;
  readonly command_type: string;
  readonly payload: Readonly<Record<string, unknown>>;
}

/** ส่วนที่คิวเป็นผู้จ่ายให้ในธุรกรรมเดียวกับแถวที่บันทึก */
export interface OfflineCommandAllocation {
  readonly client_event_id: string;
  readonly device_sequence_no: number;
  readonly occurred_at_device: string;
}

/** แถวที่เก็บจริงใน IndexedDB */
export interface OfflineCommandRecord {
  /** UUID ที่อุปกรณ์สร้าง — primary key และ idempotency key ฝั่ง server */
  readonly client_event_id: string;
  readonly device_id: string;
  /** ผู้ปฏิบัติงานที่ทำคำสั่งนี้ ใช้เพื่อ attribution เท่านั้น ไม่ใช่ authentication */
  readonly employee_id: string;
  /** ตัวนับต่อเครื่อง เพิ่มทีละ 1 ไม่ข้าม ไม่ซ้ำ — ใช้เรียงลำดับคำสั่ง */
  readonly device_sequence_no: number;
  /** เวลาจากนาฬิกาเครื่อง — บันทึกไว้เฉยๆ ห้ามใช้เรียงลำดับหรือคิดบัญชี */
  readonly occurred_at_device: string;
  readonly command_type: string;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly sync_status: LocalSyncStatus;
}

export type OfflineQueueErrorReason =
  /** เบราว์เซอร์ไม่มี IndexedDB (private window, storage ถูกปิด) */
  | 'INDEXEDDB_UNSUPPORTED'
  /** เปิดฐานข้อมูลไม่สำเร็จ เช่น SecurityError หรือ storage ถูกบล็อก */
  | 'OPEN_FAILED'
  /** แท็บอื่นถือ schema เวอร์ชันเก่าอยู่ upgrade จึงถูกบล็อก */
  | 'UPGRADE_BLOCKED'
  /** พื้นที่เก็บข้อมูลเต็ม */
  | 'QUOTA_EXCEEDED'
  | 'WRITE_FAILED'
  | 'READ_FAILED'
  /** ไม่มีแหล่งสุ่มที่ปลอดภัยพอจะสร้าง client_event_id */
  | 'NO_SECURE_RANDOM'
  /** input ไม่ครบรูปแบบ envelope */
  | 'INVALID_COMMAND'
  /** payload มี field ที่มีรูปร่างเป็น credential — ห้ามลงเครื่องเด็ดขาด */
  | 'CREDENTIAL_SHAPED_PAYLOAD';

export class OfflineQueueError extends Error {
  readonly reason: OfflineQueueErrorReason;

  constructor(reason: OfflineQueueErrorReason, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'OfflineQueueError';
    this.reason = reason;
  }
}

/**
 * ชื่อ field ที่ห้ามลงไปอยู่ใน IndexedDB ไม่ว่าจะชั้นไหนของ payload
 *
 * นี่ไม่ใช่ business validation (Work Order นี้ไม่เป็นเจ้าของ contract ของ
 * command ใดเลย) แต่เป็น security guard ตาม `docs/SECURITY.md`: เครื่องหน้างาน
 * อาจสูญหายได้ คิวจึงห้ามถือ credential, token, PIN หรือชุดสิทธิ์
 *
 * แยกสองชุดโดยเจตนา: คำยาวที่ไม่กำกวมใช้ substring ได้ ส่วนคำสั้น (`pin`,
 * `otp`) ต้องเทียบทั้งชื่อ ไม่งั้นจะไปโดน field ปกติอย่าง `shipping_no`
 */
const CREDENTIAL_KEY_SUBSTRINGS = [
  'password',
  'passcode',
  'passphrase',
  'token',
  'secret',
  'credential',
  'private_key',
  'privatekey',
  'api_key',
  'apikey',
  'access_key',
  'accesskey',
  'pin_hash',
  'pinhash',
  'rfid',
  'session_id',
  'sessionid',
  'authorization',
];

const CREDENTIAL_KEY_EXACT = new Set([
  'pin',
  'otp',
  'jwt',
  'auth',
  'permissions',
  'permission_set',
  'permissionset',
  'roles',
  'scopes',
]);

function isCredentialShapedKey(key: string): boolean {
  const normalized = key.toLowerCase();
  if (CREDENTIAL_KEY_EXACT.has(normalized)) {
    return true;
  }
  return CREDENTIAL_KEY_SUBSTRINGS.some((needle) => normalized.includes(needle));
}

/**
 * หา field ที่มีรูปร่างเป็น credential ใน payload (ลงลึกทุกชั้น)
 *
 * คืน path ของ field แรกที่เจอ เพื่อให้ผู้เรียกแก้ที่ต้นทางได้ ไม่ใช่แค่บอกว่า
 * "ไม่ผ่าน" — และคืน `null` เมื่อสะอาด
 */
export function findCredentialShapedKey(value: unknown, path: readonly string[] = []): string | null {
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const found = findCredentialShapedKey(item, [...path, String(index)]);
      if (found !== null) {
        return found;
      }
    }
    return null;
  }

  if (value === null || typeof value !== 'object') {
    return null;
  }

  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const childPath = [...path, key];
    if (isCredentialShapedKey(key)) {
      return childPath.join('.');
    }
    const found = findCredentialShapedKey(child, childPath);
    if (found !== null) {
      return found;
    }
  }

  return null;
}

function requireNonEmptyString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new OfflineQueueError('INVALID_COMMAND', `offline command ต้องมี ${field} เป็นข้อความที่ไม่ว่าง`);
  }
  return value;
}

/**
 * ตรวจรูปร่างของ input เท่าที่ envelope ต้องการ
 *
 * ตั้งใจไม่ตรวจอะไรที่ผูกกับ `command_type` เลย: contract ของแต่ละคำสั่งเป็นของ
 * Work Order ที่สร้างหน้าจอนั้น กฎที่เขียนไว้ที่นี่จะกลายเป็น source of truth
 * ที่สอง
 */
export function normalizeOfflineCommandInput(input: OfflineCommandInput): OfflineCommandInput {
  const payload = input?.payload;
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new OfflineQueueError('INVALID_COMMAND', 'offline command ต้องมี payload เป็น object');
  }

  const credentialKey = findCredentialShapedKey(payload);
  if (credentialKey !== null) {
    throw new OfflineQueueError(
      'CREDENTIAL_SHAPED_PAYLOAD',
      `payload มี field ที่มีรูปร่างเป็น credential (${credentialKey}) — ห้ามเก็บลงคิวในเครื่อง`,
    );
  }

  return {
    device_id: requireNonEmptyString(input?.device_id, 'device_id'),
    employee_id: requireNonEmptyString(input?.employee_id, 'employee_id'),
    command_type: requireNonEmptyString(input?.command_type, 'command_type'),
    payload,
  };
}

/**
 * ประกอบแถวที่จะเก็บลง IndexedDB
 *
 * คัดลอกเฉพาะ field ที่อยู่ใน envelope เท่านั้น — field แปลกปลอมที่ผู้เรียก
 * แนบมากับ input ระดับบนสุดจะถูกทิ้ง ไม่หลุดลงเครื่อง
 */
export function buildOfflineCommandRecord(
  input: OfflineCommandInput,
  allocation: OfflineCommandAllocation,
): OfflineCommandRecord {
  const normalized = normalizeOfflineCommandInput(input);
  return {
    client_event_id: allocation.client_event_id,
    device_id: normalized.device_id,
    employee_id: normalized.employee_id,
    device_sequence_no: allocation.device_sequence_no,
    occurred_at_device: allocation.occurred_at_device,
    command_type: normalized.command_type,
    payload: normalized.payload,
    sync_status: PENDING_LOCAL,
  };
}

/**
 * เรียงคำสั่งตาม `device_sequence_no` เท่านั้น
 *
 * ห้ามเรียงตามลำดับที่ใส่, ตามเวลาเครื่อง หรือตามลำดับ key ของ IndexedDB
 * เพราะนาฬิกาของ handheld กระโดดได้และ `client_event_id` เป็น UUID สุ่ม
 */
export function byDeviceSequenceNo(a: OfflineCommandRecord, b: OfflineCommandRecord): number {
  return a.device_sequence_no - b.device_sequence_no;
}
